import {
  START_BANKROLL,
  MIN_BET,
  MAX_BET,
  BLACKJACK_NUMERATOR,
  BLACKJACK_DENOMINATOR,
  DEALER_HITS_SOFT_17,
  ALLOW_DOUBLE_AFTER_SPLIT,
  HERO_SEAT_INDEX,
  NUM_PLAYER_SEATS,
} from './rules.js';
import {
  handValue,
  isNaturalBlackjack,
  isBust,
  canSplitPair,
} from './handMath.js';
import { createShoe, ensureShoeReady, drawFromShoe } from './shoe.js';
import { pickBet, randomProfile, pickAction } from './aiPlayer.js';
import { pickBoyAndGirlOpponentNames } from './israeliNames.js';

/**
 * @typedef {import('../poker/cards.js').Card} Card
 */

/**
 * @typedef {object} BjHand
 * @property {Card[]} cards
 * @property {number} wager
 * @property {boolean} doubled
 * @property {boolean} fromSplitAces
 * @property {boolean} naturalBlackjack
 * @property {boolean} stood
 * @property {boolean} busted
 */

/**
 * @typedef {object} BjSeat
 * @property {boolean} isHuman
 * @property {string} displayName
 * @property {number} bankroll
 * @property {import('./aiPlayer.js').BjAiProfile|null} profile
 * @property {BjHand[]} hands
 */

/**
 * @typedef {object} BjSettleOutcome
 * @property {number} seatIndex
 * @property {number} handIndex
 * @property {number} risk
 * @property {number} delta
 */

/**
 * @typedef {object} BjState
 * @property {'betting'|'playerTurn'|'dealer'|'payout'|'summary'} phase
 * @property {import('./shoe.js').ShoeState} shoe
 * @property {{ cards: Card[], holeHidden: boolean }} dealer
 * @property {BjSeat[]} seats
 * @property {number} pendingHumanBet
 * @property {number} activeSeatIndex
 * @property {number} activeHandIndex
 * @property {boolean} dealerBlackjack
 * @property {number} heroSnapshotAfterBets
 * @property {number} heroRoundNetChips
 * @property {string} message
 * @property {number} roundId
 * @property {{ roundId: number, outcomes: BjSettleOutcome[] }|null} pendingSettleAnim
 */

/** @param {BjHand} h */
function handDone(h) {
  if (h.naturalBlackjack) return true;
  if (h.busted) return true;
  if (h.stood) return true;
  if (h.doubled && h.cards.length === 3) return true;
  if (h.fromSplitAces && h.cards.length >= 2) return true;
  const { total } = handValue(h.cards);
  if (total === 21) return true;
  return false;
}

/** @param {BjState} state */
function allPlayerHandsDone(state) {
  for (const seat of state.seats) {
    for (const h of seat.hands) {
      if (!handDone(h)) return false;
    }
  }
  return true;
}

/** @param {BjState} state */
function anyPlayerUnbusted(state) {
  for (const seat of state.seats) {
    for (const h of seat.hands) {
      if (!h.busted) return true;
    }
  }
  return false;
}

/** @param {BjState} state */
function advanceActivePointer(state) {
  const { seats } = state;
  let s = state.activeSeatIndex;
  let hi = state.activeHandIndex;
  for (let guard = 0; guard < NUM_PLAYER_SEATS * 8; guard += 1) {
    if (s >= seats.length) {
      state.activeSeatIndex = seats.length;
      return;
    }
    const seat = seats[s];
    if (!seat.hands.length) {
      s += 1;
      hi = 0;
      continue;
    }
    if (hi >= seat.hands.length) {
      s += 1;
      hi = 0;
      continue;
    }
    const h = seat.hands[hi];
    if (!handDone(h)) {
      state.activeSeatIndex = s;
      state.activeHandIndex = hi;
      return;
    }
    hi += 1;
  }
  state.activeSeatIndex = seats.length;
}

/** @param {BjState} state */
function setFirstActive(state) {
  state.activeSeatIndex = 0;
  state.activeHandIndex = 0;
  advanceActivePointer(state);
}

/**
 * @param {Card[]} dealerCards
 */
function dealerMustHit(dealerCards) {
  const { total, soft } = handValue(dealerCards);
  if (total > 21) return false;
  if (total < 17) return true;
  if (total === 17 && soft && DEALER_HITS_SOFT_17) return true;
  return false;
}

/** @returns {BjState} */
export function createInitialState(rng = Math.random) {
  const [leftName, rightName] = pickBoyAndGirlOpponentNames(rng);
  /** @type {BjSeat[]} */
  const seats = [
    {
      isHuman: false,
      displayName: leftName,
      bankroll: START_BANKROLL,
      profile: randomProfile(rng),
      hands: [],
    },
    {
      isHuman: true,
      displayName: 'אני',
      bankroll: START_BANKROLL,
      profile: null,
      hands: [],
    },
    {
      isHuman: false,
      displayName: rightName,
      bankroll: START_BANKROLL,
      profile: randomProfile(rng),
      hands: [],
    },
  ];
  return {
    phase: 'betting',
    shoe: createShoe(rng),
    dealer: { cards: [], holeHidden: true },
    seats,
    pendingHumanBet: MIN_BET,
    activeSeatIndex: 0,
    activeHandIndex: 0,
    dealerBlackjack: false,
    heroSnapshotAfterBets: 0,
    heroRoundNetChips: 0,
    message: '',
    roundId: 0,
    pendingSettleAnim: null,
  };
}

/** @param {BjState} state @param {number} bet */
export function setPendingHumanBet(state, bet) {
  const seat = state.seats[HERO_SEAT_INDEX];
  const maxAfford = Math.min(MAX_BET, Math.max(0, seat.bankroll));
  let b = Math.max(MIN_BET, Math.min(bet, maxAfford));
  if (seat.bankroll < MIN_BET) b = seat.bankroll;
  state.pendingHumanBet = b;
}

/**
 * @param {BjState} state
 * @param {() => number} rng
 */
export function dealNewRound(state, rng = Math.random) {
  if (state.phase !== 'betting') return;
  state.message = '';
  const hero = state.seats[HERO_SEAT_INDEX];
  const humanBet = state.pendingHumanBet;
  if (hero.bankroll < MIN_BET) {
    state.message = 'gameOver';
    return;
  }
  if (humanBet < MIN_BET || humanBet > hero.bankroll) {
    state.message = 'הימור לא חוקי';
    return;
  }

  ensureShoeReady(state.shoe, rng);

  for (const seat of state.seats) {
    seat.hands = [];
  }

  for (const seat of state.seats) {
    let wager;
    if (seat.isHuman) {
      wager = humanBet;
    } else {
      wager = pickBet(seat.bankroll, seat.profile || randomProfile(rng), rng);
      if (wager < MIN_BET || seat.bankroll < wager) {
        continue;
      }
    }
    seat.bankroll -= wager;
    seat.hands.push({
      cards: [],
      wager,
      doubled: false,
      fromSplitAces: false,
      naturalBlackjack: false,
      stood: false,
      busted: false,
    });
  }

  if (!state.seats[HERO_SEAT_INDEX].hands.length) {
    state.message = 'שגיאה';
    return;
  }

  state.roundId += 1;
  const participating = state.seats.filter((s) => s.hands.length > 0);
  state.heroSnapshotAfterBets = hero.bankroll;
  state.dealer = { cards: [], holeHidden: true };
  state.dealerBlackjack = false;
  state.heroRoundNetChips = 0;

  for (const seat of participating) {
    const c = drawFromShoe(state.shoe);
    if (c) seat.hands[0].cards.push(c);
  }
  const dUp = drawFromShoe(state.shoe);
  if (dUp) state.dealer.cards.push(dUp);

  for (const seat of participating) {
    const c = drawFromShoe(state.shoe);
    if (c) seat.hands[0].cards.push(c);
  }
  const hole = drawFromShoe(state.shoe);
  if (hole) state.dealer.cards.push(hole);

  for (const seat of participating) {
    const h = seat.hands[0];
    if (isNaturalBlackjack(h.cards)) {
      h.naturalBlackjack = true;
    }
  }

  if (isNaturalBlackjack(state.dealer.cards)) {
    state.dealerBlackjack = true;
    state.dealer.holeHidden = false;
    settleRound(state);
    return;
  }

  for (const seat of participating) {
    for (const h of seat.hands) {
      if (h.naturalBlackjack) {
        h.stood = true;
      }
    }
  }

  state.phase = 'playerTurn';
  setFirstActive(state);
  if (state.activeSeatIndex >= state.seats.length || allPlayerHandsDone(state)) {
    state.phase = 'dealer';
    runDealerPhase(state, rng);
  }
}

/**
 * @param {BjHand} h
 * @param {BjSeat} seat
 * @param {BjState} state
 */
function canDoubleHand(h, seat, state) {
  if (state.phase !== 'playerTurn') return false;
  if (h.cards.length !== 2 || h.doubled || h.busted || h.stood) return false;
  if (h.naturalBlackjack) return false;
  if (h.fromSplitAces && !ALLOW_DOUBLE_AFTER_SPLIT) return false;
  return seat.bankroll >= h.wager;
}

/**
 * @param {BjHand} h
 * @param {BjSeat} seat
 */
function canSplitHand(h, seat) {
  return canSplitPair(h.cards, seat.hands.length) && seat.bankroll >= h.wager;
}

/**
 * @param {BjState} state
 * @param {number} seatIndex
 * @param {number} handIndex
 */
export function legalActions(state, seatIndex, handIndex) {
  if (state.phase !== 'playerTurn') return [];
  const seat = state.seats[seatIndex];
  const h = seat.hands[handIndex];
  if (!h || handDone(h)) return [];
  if (seatIndex !== state.activeSeatIndex || handIndex !== state.activeHandIndex) return [];

  const out = [];
  const { total } = handValue(h.cards);
  if (h.fromSplitAces) {
    return ['stand'];
  }
  if (h.doubled) {
    return [];
  }
  if (total > 21) {
    return [];
  }
  if (total === 21) {
    return ['stand'];
  }
  out.push('hit');
  out.push('stand');
  if (canDoubleHand(h, seat, state)) out.push('double');
  if (canSplitHand(h, seat)) out.push('split');
  return out;
}

/**
 * @param {BjState} state
 * @param {'hit'|'stand'|'double'|'split'} action
 * @param {() => number} rng
 */
export function applyPlayerAction(state, action, rng = Math.random) {
  if (state.phase !== 'playerTurn') return;
  const sIdx = state.activeSeatIndex;
  const hIdx = state.activeHandIndex;
  const seat = state.seats[sIdx];
  const h = seat.hands[hIdx];
  if (!h || handDone(h)) return;

  const allowed = legalActions(state, sIdx, hIdx);
  if (!allowed.includes(action)) return;

  if (action === 'stand') {
    h.stood = true;
  } else if (action === 'hit') {
    const c = drawFromShoe(state.shoe);
    if (c) h.cards.push(c);
    if (isBust(h.cards)) h.busted = true;
    else if (handValue(h.cards).total === 21) h.stood = true;
  } else if (action === 'double') {
    seat.bankroll -= h.wager;
    h.wager *= 2;
    h.doubled = true;
    const c = drawFromShoe(state.shoe);
    if (c) h.cards.push(c);
    if (isBust(h.cards)) h.busted = true;
    else h.stood = true;
  } else if (action === 'split') {
    seat.bankroll -= h.wager;
    const c1 = h.cards[0];
    const c2 = h.cards[1];
    const fromAces = c1.rank === 14;
    const newHand1 = {
      cards: [c1],
      wager: h.wager,
      doubled: false,
      fromSplitAces: fromAces,
      naturalBlackjack: false,
      stood: false,
      busted: false,
    };
    const newHand2 = {
      cards: [c2],
      wager: h.wager,
      doubled: false,
      fromSplitAces: fromAces,
      naturalBlackjack: false,
      stood: false,
      busted: false,
    };
    const d1 = drawFromShoe(state.shoe);
    const d2 = drawFromShoe(state.shoe);
    if (d1) newHand1.cards.push(d1);
    if (d2) newHand2.cards.push(d2);
    if (fromAces) {
      newHand1.stood = true;
      newHand2.stood = true;
    } else {
      if (isBust(newHand1.cards)) newHand1.busted = true;
      else if (handValue(newHand1.cards).total === 21) newHand1.stood = true;
      if (isBust(newHand2.cards)) newHand2.busted = true;
      else if (handValue(newHand2.cards).total === 21) newHand2.stood = true;
    }
    seat.hands.splice(hIdx, 1, newHand1, newHand2);
  }

  advanceActivePointer(state);
  if (state.activeSeatIndex >= state.seats.length || allPlayerHandsDone(state)) {
    state.phase = 'dealer';
    runDealerPhase(state, rng);
  }
}

/**
 * @param {BjState} state
 * @param {() => number} rng
 */
function runDealerPhase(state, rng) {
  const needPlay = anyPlayerUnbusted(state);
  state.dealer.holeHidden = false;
  if (needPlay) {
    while (dealerMustHit(state.dealer.cards)) {
      const c = drawFromShoe(state.shoe);
      if (!c) break;
      state.dealer.cards.push(c);
    }
  }
  settleRound(state);
}

/** @param {BjState} state */
function settleRound(state) {
  const dealer = state.dealer.cards;
  const dBust = isBust(dealer);
  const dVal = dBust ? 0 : handValue(dealer).total;
  const dNat = state.dealerBlackjack;

  /** @type {{ seatIndex: number, handIndex: number, risk: number, delta: number }[]} */
  const outcomes = [];

  for (let si = 0; si < state.seats.length; si += 1) {
    const seat = state.seats[si];
    for (let hi = 0; hi < seat.hands.length; hi += 1) {
      const h = seat.hands[hi];
      const risk = h.wager;
      let delta = 0;
      if (dNat) {
        if (h.naturalBlackjack) delta = 0;
        else delta = -risk;
      } else if (h.naturalBlackjack) {
        delta = Math.floor((risk * BLACKJACK_NUMERATOR) / BLACKJACK_DENOMINATOR);
      } else if (h.busted) {
        delta = -risk;
      } else if (dBust) {
        delta = risk;
      } else if (dVal > handValue(h.cards).total) {
        delta = -risk;
      } else if (dVal < handValue(h.cards).total) {
        delta = risk;
      } else {
        delta = 0;
      }
      outcomes.push({ seatIndex: si, handIndex: hi, risk, delta });
      seat.bankroll += risk + delta;
    }
  }

  state.pendingSettleAnim = { roundId: state.roundId, outcomes };

  const hero = state.seats[HERO_SEAT_INDEX];
  state.heroRoundNetChips = hero.bankroll - state.heroSnapshotAfterBets;
  state.phase = 'summary';
}

/**
 * @param {BjState} state
 */
export function continueFromSummary(state) {
  if (state.phase !== 'summary') return;
  if (state.seats[HERO_SEAT_INDEX].bankroll < MIN_BET) {
    state.message = 'gameOver';
    return;
  }
  state.phase = 'betting';
  state.dealer = { cards: [], holeHidden: true };
  state.dealerBlackjack = false;
  for (const seat of state.seats) {
    seat.hands = [];
  }
  state.pendingSettleAnim = null;
  state.message = '';
}

/** @param {BjState} state */
export function isHeroBankrupt(state) {
  return state.seats[HERO_SEAT_INDEX].bankroll < MIN_BET;
}

/**
 * @param {BjState} state
 * @param {() => number} rng
 */
export function runAiStep(state, rng = Math.random) {
  if (state.phase !== 'playerTurn') return;
  const sIdx = state.activeSeatIndex;
  const hIdx = state.activeHandIndex;
  const seat = state.seats[sIdx];
  if (seat.isHuman) return;
  const h = seat.hands[hIdx];
  if (!h || handDone(h)) return;

  const up = state.dealer.cards[0] || null;
  let act = pickAction(
    {
      cards: h.cards,
      wager: h.wager,
      doubled: h.doubled,
      fromSplitAces: h.fromSplitAces,
      canDouble: canDoubleHand(h, seat, state),
      canSplit: canSplitHand(h, seat),
      bankroll: seat.bankroll,
    },
    up,
    seat.profile || randomProfile(rng),
    rng,
  );
  const allowed = legalActions(state, sIdx, hIdx);
  if (!allowed.includes(act)) {
    if (act === 'double' && allowed.includes('hit')) act = 'hit';
    else if (act === 'split' && allowed.includes('hit')) act = 'hit';
    else act = allowed[0] || 'stand';
  }
  applyPlayerAction(state, act, rng);
}

export { isNaturalBlackjack, handValue };
export {
  START_BANKROLL,
  MIN_BET,
  MAX_BET,
  BET_PRESETS,
  BET_STEP,
  HERO_SEAT_INDEX,
  NUM_PLAYER_SEATS,
  TABLE_ID,
} from './rules.js';
